import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, CardMedia, Button, Chip, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import identityImage from '../Images/identity.png';
import metaImage from '../Images/meta_api.png'; 
import ecommerceImage from '../Images/ecommerce_opt.png'; 

const Projects = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const projects = [
    {
      title: t('projects.identity.title'),
      description: t('projects.identity.description'),
      image: identityImage,
      technologies: t('projects.identity.technologies', { returnObjects: true }),
      github: '#',
      demo: '#'
    },
    { 
      title: t('projects.meta.title'),
      description: t('projects.meta.description'),
      image: metaImage,
      technologies: t('projects.meta.technologies', { returnObjects: true }),
      github: '#',
      demo: '#'
    },
    {
      title: t('projects.ecommerce.title'),
      description: t('projects.ecommerce.description'),
      image: ecommerceImage,
      technologies: t('projects.ecommerce.technologies', { returnObjects: true }),
      github: '#',
      demo: '#'
    }
  ];

  return (
    <Box
      id="projects"
      sx={{
        py: 8,
        background: 'linear-gradient(180deg, #050810 0%, #0a1428 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 2,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #ffffff 30%, #22e165 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('projects.title')}
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                mb: 6,
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 500
              }}
            >
              {t('projects.subtitle')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {projects.map((project, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div variants={itemVariants}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'rgba(34, 225, 101, 0.05)',
                      border: '1px solid rgba(34, 225, 101, 0.1)',
                      borderRadius: 4,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        background: 'rgba(34, 225, 101, 0.1)',
                        borderColor: 'rgba(34, 225, 101, 0.2)',
                        '& .MuiCardMedia-root': {
                          transform: 'scale(1.05)'
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '56.25%', // 16:9 Aspect Ratio
                        overflow: 'hidden',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)'
                      }}
                    >
                      <CardMedia
                        component="img"
                        image={project.image}
                        alt={project.title}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.5s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          mb: 2
                        }}
                      >
                        {project.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          mb: 3,
                          lineHeight: 1.6
                        }}
                      >
                        {project.description}
                      </Typography>
                      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {project.technologies.map((tech, techIndex) => (
                          <Chip
                            key={techIndex}
                            label={tech}
                            size="small"
                            sx={{
                              background: 'rgba(34, 225, 101, 0.1)',
                              border: '1px solid rgba(34, 225, 101, 0.2)',
                              color: 'white',
                              '&:hover': {
                                background: 'rgba(34, 225, 101, 0.2)'
                              }
                            }}
                          />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<GitHubIcon />}
                          href={project.github}
                          target="_blank"
                          sx={{
                            borderColor: 'rgba(34, 225, 101, 0.5)',
                            color: 'white',
                            '&:hover': {
                              borderColor: '#22e165',
                              background: 'rgba(34, 225, 101, 0.1)'
                            }
                          }}
                        >
                          {t('projects.viewCode')}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LaunchIcon />}
                          href={project.demo}
                          target="_blank"
                          sx={{
                            borderColor: 'rgba(34, 225, 101, 0.5)',
                            color: 'white',
                            '&:hover': {
                              borderColor: '#22e165',
                              background: 'rgba(34, 225, 101, 0.1)'
                            }
                          }}
                        >
                          {t('projects.viewDemo')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Projects;